// src/lib/managers/mail/utils/computeAnalysisFloor.ts

/**
 * AB#1621 — Calcul (PUR) du plancher d'analyse rétrospective.
 *
 * Entrées du calcul du « mail-frontière » du budget byMail/byDay (cf. INVESTIGATION §5.2/§5.6).
 */
export type AnalysisFloorInput = {
    /** Nombre d'ids budgétaires effectivement récupérés (`getMailIdsSince`). */
    budgetCount: number;
    /** Plafond licence (`initialAnalysis.byMail`). */
    byMail: number;
    /** Fenêtre licence en jours (`initialAnalysis.byDay`). */
    byDay: number;
    /** Ancre = `registrationDate` (ou `now` en repli). */
    anchorDate: Date;
    /** `sentDateTime` du mail-frontière (dernier id, ordre DESC) ; `null` si introuvable/sans date. */
    frontierSentDate: Date | null;
};

type AnalysisFloorSource = "mail-frontière" | "provisoire";

export type AnalysisFloorResult = {
    floor: Date;
    source: AnalysisFloorSource;
};

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Dérive le plancher d'analyse (borne basse de la fenêtre budgétaire). Fonction **pure** (déterministe,
 * sans I/O) — testable en isolation (§5.6).
 *
 * Robustesse « anti-date-pourrie » : on ne resserre le plancher vers le mail-frontière QUE lorsqu'on sait
 * **positivement** que `byMail` a borné (`budgetCount >= byMail`) ET qu'on a une date de frontière valide
 * **et ≤ ancre**. Toute autre situation ⇒ plancher **provisoire** `anchor − byDay` (borne la plus large,
 * jamais trop récente ⇒ aucun utilisateur bloqué).
 */
export function computeAnalysisFloor({
    budgetCount,
    byMail,
    byDay,
    anchorDate,
    frontierSentDate,
}: AnalysisFloorInput): AnalysisFloorResult {
    const provisionalFloor = new Date(anchorDate.getTime() - byDay * DAY_MS);

    const byMailBounded = budgetCount >= byMail;
    const frontierIsValid =
        frontierSentDate !== null && !isNaN(frontierSentDate.getTime());
    // Invariant de sûreté : jamais un plancher plus récent que l'ancre (1ʳᵉ connexion).
    const frontierNotTooRecent =
        frontierIsValid && frontierSentDate!.getTime() <= anchorDate.getTime();

    if (byMailBounded && frontierNotTooRecent) {
        return { floor: frontierSentDate!, source: "mail-frontière" };
    }

    return { floor: provisionalFloor, source: "provisoire" };
}
